import React, { useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import IconFont from '@meitu/xdesign-icon-font';
import { getLocalIdentity } from '@meitu/xdesign-identity';
import { CommonModal } from '@/ComUI';
import { useConfig } from '@/configProvider';
import { useLocaleStore } from '@/i18n';
import Loading from '@/loading';
import { ITeamIdentity } from '@/types';
import { changeIdentity, closeIdentitySwitchPopup, useTeamInfoState } from '..';
import { openCreateTeamModal } from '../createModal';
import { TypeTrack } from '../identitySwitch/shared';
import IdentityRow from './identityRow';
import './index.less';

// 展示身份切换弹窗事件
export const SHOW_IDENTITY_SWITCH_MODAL = 'xdesign-widget-show-identity-switch-modal';

export type SHOW_IDENTITY_SWITCH_MODAL_EVENT_DATA = {
    showCloseBtn?: boolean;
    // 隐藏创建按钮
    hideCreateBtn?: boolean;
    /** 创建团队完后, 是否切换身份, 默认为切换 */
    noSwitchIdentity?: boolean;
    filterIdentities?: (identity: ITeamIdentity) => boolean;
    onSwitchComplete?: (
        identity: {
            oid: number;
            groupId: number;
            ouid: number;
        },
        ext: {
            isFromCreate: boolean;
        },
    ) => void;
    /** 自定义 创建团队成功的entrance埋点参数 */
    create_team_success_entrance?: string;
    project?: string;
    /** 顶部的提示语 */
    topTip?: string;
    // 隐藏选中态
    hideSelectedStatus?: boolean;
};

// 隐藏身份切换弹窗事件
export const HIDDEN_IDENTITY_SWITCH_MODAL = 'xdesign-widget-hidden-identity-switch-modal';

/**
 * 登陆后身份选择
 */
interface Props extends SHOW_IDENTITY_SWITCH_MODAL_EVENT_DATA {
    onClose?: () => void;
    onTrack?: TypeTrack;
}

const LoginIdentitySwitchModal = ({
    showCloseBtn = false,
    hideCreateBtn,
    create_team_success_entrance,
    project = '',
    noSwitchIdentity = false,
    filterIdentities = () => true,
    onSwitchComplete,
    onClose,
    onTrack,
    topTip,
    hideSelectedStatus,
}: Props) => {
    const { $t } = useLocaleStore();
    const { loginIdentitySwitchModalConfig } = useConfig();
    const teamInfo = useTeamInfoState();

    // 关闭
    const handleConfirm = (oid: number, groupId: number, ouid: number) => {
        if (!teamInfo) return;

        onSwitchComplete?.({ oid, groupId, ouid }, { isFromCreate: false });
        onClose?.();

        if (!noSwitchIdentity) {
            changeIdentity({ ouid, oid, groupId });
        }
    };

    useEffect(() => {
        if (teamInfo) {
            Loading.hidden();
        } else {
            Loading.show($t((v) => v.Tips_Loading));
        }
    }, [teamInfo]);

    const displayIdentities = teamInfo?.identity.list.filter(filterIdentities) ?? [];
    const personlIsShow = displayIdentities.some((it) => it.type === 0);

    if (!teamInfo) return null;

    return (
        <CommonModal
            destroyOnClose={true}
            open
            zIndex={1000000001} // 这么大是因为业务引导弹窗库的层级是1000000000
            className="Widget-teamSpaceLoginIdentitySwitchModal"
        >
            <div className="Widget teamSpaceLoginIdentitySwitchModal">
                {showCloseBtn && (
                    <div className="close" onClick={closeIdentitySwitchPopup}>
                        <IconFont fontSize={14} name="CrossBlack" color="var(--content-system-tertiary)" />
                    </div>
                )}
                <div className="title">
                    {personlIsShow ? $t((v) => v.Text_SelectIdentity) : $t((v) => v.Text_SelectTeamIdentity)}
                </div>
                {loginIdentitySwitchModalConfig.showEditorSaveTip && (
                    <div className="editing-save-tip">
                        <IconFont
                            style={{ marginRight: 4 }}
                            name="InfoCircleBoldFill"
                            fontSize={16}
                            color="var(--content-system-info-icon-regular, #8CC0FF)"
                        />
                        {topTip ?? (
                            <>
                                {$t((v, { r }) =>
                                    r(v.Text_EditingSaveLocation, [
                                        getLocalIdentity()?.oid === 0
                                            ? $t((v) => v.Text_PersonalDesigner)
                                            : (teamInfo.identity.list.find((it) => it.oid === getLocalIdentity()?.oid)
                                                  ?.name ?? ''),
                                    ]),
                                )}
                            </>
                        )}
                    </div>
                )}
                <div
                    className="list"
                    style={{
                        height: teamInfo.identity.list.length > 3 ? 240 : teamInfo.identity.list.length * 74,
                    }}
                >
                    <Scrollbars className="loginIdentitySwitchModal-scrollbars">
                        {displayIdentities.map((identity, key) => (
                            <div key={key} style={{ margin: '0 16px' }}>
                                <IdentityRow
                                    identity={identity}
                                    isSelected={!hideSelectedStatus && identity.oid === getLocalIdentity()?.oid}
                                    onClick={() => handleConfirm(identity.oid, identity.groups[0].id, identity.ouid)}
                                />
                            </div>
                        ))}
                    </Scrollbars>
                </div>

                {!hideCreateBtn ? (
                    <div
                        className="team-create-box"
                        onClick={() => {
                            closeIdentitySwitchPopup();
                            openCreateTeamModal({
                                onTrack,
                                create_team_success_entrance,
                                project,
                                noSwitchIdentity,
                                onCreatedComplete: (data) =>
                                    onSwitchComplete?.(
                                        {
                                            oid: data.oid,
                                            groupId: data.group_id,
                                            ouid: data.ouid,
                                        },
                                        {
                                            isFromCreate: true,
                                        },
                                    ),
                            });
                        }}
                    >
                        <div className="team-create-btn">
                            <div className="team-create-icon">
                                <IconFont fontSize={18} name="PlusBlack" color="var(--content-web-secondary)" />
                            </div>
                            <div className="team-create-text">{$t((v) => v.Text_CreateNewTeamFree)}</div>
                        </div>
                    </div>
                ) : null}
            </div>
        </CommonModal>
    );
};

export default React.memo(LoginIdentitySwitchModal);
